import type { LucideIcon } from "lucide-react";
import {
  Bot,
  Crown,
  FileText,
  KeyRound,
  Layers,
} from "lucide-react";
import type { ProFeatureId } from "@/types/billing.types";

export type ProFunnelContent = {
  kicker: string;
  headline: string;
  subheadline: string;
  bullets: string[];
  cta: string;
  icon: LucideIcon;
};

export const PRO_FEATURE_FUNNELS: Record<ProFeatureId, ProFunnelContent> = {
  multiple_startups: {
    kicker: "Scale your portfolio",
    headline: "Run more than one burn chart",
    subheadline:
      "Your first startup is free. Pro unlocks separate models for each company, side project, or client engagement.",
    bullets: [
      "Keep financial data isolated per startup",
      "Switch between charts from the header in one click",
      "Invite collaborators per startup with team sharing",
    ],
    cta: "Upgrade to add startups",
    icon: Layers,
  },
  ai_advisor: {
    kicker: "Grounded in your data",
    headline: "Ask your burn chart anything",
    subheadline:
      "The AI advisor reads your expenses, runway, and projections — not generic startup blog posts.",
    bullets: [
      "Get runway and burn answers with your actual numbers",
      "Stress-test hiring, pricing, and fundraising scenarios",
      "Chat history saved per startup",
    ],
    cta: "Unlock AI advisor",
    icon: Bot,
  },
  business_plan: {
    kicker: "Investor-ready output",
    headline: "Generate your AI business plan",
    subheadline:
      "Investor links and live charts are free. Pro adds AI-written narrative sections grounded in your burn chart and founder drafts.",
    bullets: [
      "Financial snapshot and charts pulled from your model automatically",
      "Polish your section drafts into professional investor prose",
      "Export a full plan document to PDF for your data room",
    ],
    cta: "Generate with Pro",
    icon: FileText,
  },
  api_access: {
    kicker: "Build on BurnChart",
    headline: "Connect your tools with the API",
    subheadline:
      "Sync expenses, revenues, projections, and business plan inputs from scripts, internal tools, or your own stack.",
    bullets: [
      "Scoped API keys with read-only or full access",
      "Documented endpoints for startups, financials, and plans",
      "Automate imports instead of manual spreadsheet updates",
    ],
    cta: "Unlock API access",
    icon: KeyRound,
  },
};

export const PRO_UPGRADE_DEFAULT_FUNNEL: ProFunnelContent = {
  kicker: "BurnChart Pro",
  headline: "Upgrade to unlock the full toolkit",
  subheadline:
    "Keep modeling your first startup for free. Pro adds multiple startups, AI advisor, business plan generation, and API access.",
  bullets: [
    "Multiple startups on one account",
    "AI financial advisor grounded in your data",
    "AI business plan generation and PDF export",
    "Programmatic API access with API keys",
  ],
  cta: "Upgrade with Stripe Checkout",
  icon: Crown,
};

export function getProFunnelContent(featureId?: ProFeatureId): ProFunnelContent {
  if (!featureId) {
    return PRO_UPGRADE_DEFAULT_FUNNEL;
  }
  return PRO_FEATURE_FUNNELS[featureId];
}
